import React, { useCallback, useEffect, useRef } from 'react'
import { Checkbox, PlaceSuggest, useAtSize, useZionFormContext } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'

const placeFieldCss = css`
  display: grid;
  grid-template-columns: 1fr min-content;
  --text-primary: var(--border-primary);

  & > div {
    max-width: 700px;
  }

  & .autosuggest-wrapper {
    max-height: 64px;
  }
`

const exactCheckboxCss = css`
  margin: 28px 0 0 16px;
`

export default function PlaceField({
  name,
  type,
  orientation,
}: {
  name?: string
  type?: string
  orientation?: string
}): JSX.Element {
  const atSize = useAtSize()
  const [t] = useTranslation()
  const exactLabel = t('ancestors.exact.option', 'Exact')
  const { makeFormComponent, setFieldValue, values } = useZionFormContext()
  const { showMatchExactly, matchAllExactly } = values
  const checkboxName = `${name}.placeExact`

  const isMobile = atSize({ default: true, md: false })
  const fullWidth = isMobile || orientation === 'vertical'
  const placeValues = values[name]?.place
  const placeLabel = fullWidth
    ? t('ancestors.place.condensed-label', 'Place')
    : t(`ancestors.${type}.place.label`, 'Place')
  const initialSelectedItem = placeValues?.primaryText
    ? { isStandard: values[name]?.place?.isStandard, primaryText: placeValues?.primaryText }
    : undefined
  const initialRenderRef = useRef(true)

  useEffect(() => {
    let cancelled = false
    if (!cancelled && initialRenderRef.current) {
      setFieldValue(name, values[name] || { place: { primaryText: '' } })
      initialRenderRef.current = false
    }

    return () => {
      cancelled = true
    }
  }, [name, setFieldValue, values])

  const handleClick = useCallback(
    ({ target }) => {
      if (matchAllExactly && !target.checked) {
        setFieldValue('matchAllExactly', false)
      }
    },
    [matchAllExactly, setFieldValue]
  )
  return makeFormComponent(
    <div css={placeFieldCss}>
      <PlaceSuggest
        label={placeLabel}
        name={`${name}.place`}
        data-testid={`${name}-place`}
        initialSelectedItem={initialSelectedItem}
        handleInput
      />

      {showMatchExactly && (
        <div css={exactCheckboxCss}>
          <Checkbox
            data-testid={`${checkboxName}.option`}
            onClick={handleClick}
            label={exactLabel}
            name={checkboxName}
          />
        </div>
      )}
    </div>
  )
}
